import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import type { ButtonEventProps } from '@/models/analytics/features/ui';
import { trackScreenViewed as trackScreenViewedWrapper } from '@heidi/analytics/events';
import { ScreenName } from '@heidi/analytics/vocabulary';
import type { TrackDictateTourBannerEvent } from '@heidi/dictate/contracts';
import {
  type DictationLogsAnalyticsPorts,
  type DictationLogsButtonClickedEventProps,
  type DictationLogsSpotlightDismissedEventProps,
} from '@heidi/dictate/contracts';
import { trackSnippetCreated, trackSnippetEdited } from '@heidi/templates/data';

/**
 * App-side emit hosts for the Dictate-history surfaces (`@heidi/dictate`).
 * The tracking hooks that build the payloads moved into the package and reach
 * these through the analytics seam.
 *
 * ROUTING: NO `options` on the button clicks or the spotlight dismissal — the
 * Amplitude-stays-on decision these dispatches have always carried (Amplitude stays on
 * alongside Snowplow / Datadog while the dashboards live there). These hosts
 * deliberately do NOT route through the `trackButtonClicked` helper, which
 * forces `amplitude: false`. `screen_viewed` binds to the app wrapper, which
 * passes none either.
 *
 * The enum members, not string literals: `event_name` discriminates
 * `TrackingEventV2` on string ENUM members. The contracts declare the same
 * wire values; the host's suite asserts the pairing.
 */

export const trackDictationLogsButtonClicked: DictationLogsAnalyticsPorts['trackButtonClicked'] =
  (props: DictationLogsButtonClickedEventProps) => {
    track({
      event_name: TrackingEventName.ButtonClicked,
      // `screen_name` / `action` are the vocabulary enums' values on the wire;
      // the enum types are nominal, so this re-tags rather than converts. The
      // suite asserts the emitted strings.
      props: props as unknown as ButtonEventProps,
    });
  };

export const trackDictationLogsSpotlightDismissed: DictationLogsAnalyticsPorts['trackSpotlightDismissed'] =
  (props: DictationLogsSpotlightDismissedEventProps) => {
    track({
      event_name: TrackingEventName.DictationLogsSpotlightDismissed,
      props,
    });
  };

/**
 * The tour banner's contract events adapted to the app's `track()` — moved
 * here from the logs page component, which was the last thing tying that
 * deferred surface to the app's analytics modules. Routing per event is the
 * banner's own: the impression passes no options, the CTA click passes
 * `{ amplitude: false }`.
 */
export const trackDictateTourBannerEvent: TrackDictateTourBannerEvent = (event) => {
  switch (event.event_name) {
    case 'screen_viewed':
      track({
        event_name: TrackingEventName.ScreenViewed,
        props: { screen_name: ScreenName.DictateTourBanner },
      });
      return;
    case 'dictate_tour_banner_cta_clicked':
      track({
        event_name: TrackingEventName.DictateTourBannerCtaClicked,
        props: {},
        options: { amplitude: false },
      });
      return;
    default: {
      const _exhaustive: never = event;
      throw new Error(
        `Unhandled dictate tour banner event: ${JSON.stringify(_exhaustive)}`
      );
    }
  }
};

/** `modal_viewed` for the dictate → scribe cross-sell spotlight (Amplitude off). */
export const trackDictateCrossSellViewed: DictationLogsAnalyticsPorts['trackCrossSellViewed'] =
  () => {
    track({
      event_name: TrackingEventName.ModalViewed,
      props: { screen_name: ScreenName.ScribeCrossSellSpotlight },
      options: { amplitude: false },
    });
  };

/** One bundle for the bootstrap wiring. A module constant, so it is the same
 * object every time and cannot re-fire an effect that depends on it. */
export const dictationLogsAnalyticsPorts: DictationLogsAnalyticsPorts = {
  trackButtonClicked: trackDictationLogsButtonClicked,
  trackSpotlightDismissed: trackDictationLogsSpotlightDismissed,
  // The snippet wrappers BY REFERENCE — their legacy Segment routing is theirs.
  trackSnippetCreated,
  trackSnippetEdited,
  trackTourBannerEvent: trackDictateTourBannerEvent,
  trackCrossSellViewed: trackDictateCrossSellViewed,
  // The one adapted member: the wrapper's param types the screen name as the
  // `ScreenName` enum the inert door cannot name. The hooks pass the enum's
  // own value, so the cast re-tags rather than converts.
  trackScreenViewed: ({ screen_name }) => {
    trackScreenViewedWrapper({ screen_name: screen_name as ScreenName });
  },
};
